import React, {
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import {Box, Flex, useBreakpointValue, useDisclosure} from '@tempo/core';
import Navigation from '@deezer/next/components/Navigation';
import Sidebar from '@components/Sidebar';
import useMetaKeyboardSequence from '@deezer/next/shared/hooks/useMetaKeyboardSequence';
import SidebarProvider from '@components/Sidebar/SidebarProvider';
import MobileSidebar from '@components/Sidebar/MobileSidebar';
import MobileMenuHeader from '@components/Sidebar/MobileMenuHeader';
import {useRouter} from 'next/router';
import {useBoolean} from '@deezer/next/shared/hooks/useBoolean';

type NavigationLayoutProps = {
	children: React.ReactNode;
	useHorizontalNavbar?: boolean;
};

const HEADER_HEIGHT = 65;
const HEADER_HIDE_THRESHOLD = 50;

const NavigationLayout = ({
	children,
	useHorizontalNavbar,
}: NavigationLayoutProps) => {
	const mainRef = useRef<HTMLDivElement>(null);
	const router = useRouter();
	const showMobileMenu = useBreakpointValue({
		base: true,
		md: false,
	});
	const [showSidebar, setShowSidebar] = useBoolean(true);
	const [headerScroll, setHeaderScroll] = useState(0);
	const {
		isOpen: isMobileMenuOpen,
		onClose: onMobileMenuClose,
		onToggle: onMobileMenuToggle,
	} = useDisclosure();

	const handleHeaderScroll = useCallback((scrollElement: HTMLElement) => {
		const {scrollTop, scrollHeight} = scrollElement;
		const {height} = document.body.getBoundingClientRect();

		if (scrollHeight - HEADER_HEIGHT - HEADER_HIDE_THRESHOLD < height) {
			setHeaderScroll(0);
		} else {
			setHeaderScroll(scrollTop);
		}
	}, []);

	const {headerTranslateY, contentMaxHeight} = useMemo(() => {
		const translateY = Math.min(
			Math.max(headerScroll - HEADER_HIDE_THRESHOLD, 0),
			HEADER_HEIGHT,
		);
		return {
			headerTranslateY: `translateY(-${translateY}px)`,
			contentMaxHeight: `calc(100% - ${HEADER_HEIGHT}px + ${translateY}px)`,
		};
	}, [headerScroll]);

	useMetaKeyboardSequence(
		[{key: 'g'}, {key: 'b'}],
		() => {
			setShowSidebar.toggle();
		},
		[],
	);

	useLayoutEffect(() => {
		onMobileMenuClose();
	}, [onMobileMenuClose, showMobileMenu]);

	useEffect(() => {
		(() => {
			setHeaderScroll(0);
		})();
	}, [router.pathname]);

	if (!useHorizontalNavbar) {
		return (
			<SidebarProvider
				toggleSidebar={
					showMobileMenu ? onMobileMenuToggle : setShowSidebar.toggle
				}
				setHeaderScroll={handleHeaderScroll}
			>
				<Box
					bg="linear-gradient(180deg, var(--tempo-colors-background-neutral-primary-default) 0%, var(--tempo-colors-background-neutral-secondary-default) 100%);"
					height="100%"
					width="100%"
					position="fixed"
				>
					{showMobileMenu ? (
						<>
							<MobileSidebar
								isOpen={isMobileMenuOpen}
								onClose={onMobileMenuClose}
							/>
						</>
					) : (
						<Sidebar
							display={{base: 'none', md: 'flex'}}
							width={
								showSidebar ? 'd4c.navbar.width' : 'd4c.navbar.collapsed.width'
							}
							collapsed={!showSidebar}
							position="absolute"
							top={0}
							bottom={0}
							overflow="hidden"
							transition="width 200ms"
							showFold={true}
						/>
					)}

					<Flex height="100%" direction="column">
						{showMobileMenu && (
							<MobileMenuHeader transform={headerTranslateY} />
						)}
						<Box
							py="spacing.s"
							pe="spacing.s"
							ps={{base: 'spacing.s', md: 0}}
							ms={{
								base: 0,
								md: showSidebar
									? 'd4c.navbar.width'
									: 'd4c.navbar.collapsed.width',
							}}
							transition="margin 200ms"
							flex={1}
							maxHeight={showMobileMenu ? contentMaxHeight : '100%'}
							transform={showMobileMenu ? headerTranslateY : 'none'}
						>
							<Box
								ref={mainRef}
								bg="background.neutral.primary.default"
								borderRadius="s"
								borderColor="divider.neutral.primary.default"
								borderStyle="solid"
								borderWidth="1px"
								overflow="auto"
								height="100%"
							>
								{children}
							</Box>
						</Box>
					</Flex>
				</Box>
			</SidebarProvider>
		);
	}

	return (
		<>
			<Navigation />
			<Box
				pt={{base: 'd4c.navbar.height.base', sm: 'd4c.navbar.height.sm'}}
				pb={12}
			>
				{children}
			</Box>
		</>
	);
};

export default NavigationLayout;
